import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Check, ArrowRight, Calendar as CalIcon } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { useLang } from "../i18n/LanguageContext";
import { Reveal, MaskedLines } from "../components/Motion";
import Seo from "../components/Seo";
import FaqSection from "../components/FaqSection";
import { Calendar } from "../components/ui/calendar";
import { postContact, getAvailability, createBooking } from "../lib/api";

const inputCls =
  "w-full border-b border-white/20 bg-transparent py-3 text-base text-white placeholder:text-white/35 focus:border-champagne focus:outline-none transition-colors";
const labelCls = "mb-2 block text-[10px] uppercase tracking-[0.25em] text-white/45";

const ContactForm = () => {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", subject: "", area: "", message: "" });
  const [status, setStatus] = useState("idle");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      await postContact(form);
      setStatus("success");
      setForm({ name: "", email: "", subject: "", area: "", message: "" });
    } catch {
      setStatus("idle");
      toast.error(t.contact.error);
    }
  };

  if (status === "success") {
    return (
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-3 border border-champagne/40 px-6 py-8 text-champagne" data-testid="contact-success">
        <Check size={22} /> <span className="text-base">{t.contact.success}</span>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-8" data-testid="contact-form">
      <div className="grid gap-8 sm:grid-cols-2">
        <div>
          <label className={labelCls}>{t.contact.name}</label>
          <input required value={form.name} onChange={set("name")} data-testid="contact-name" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>{t.contact.email}</label>
          <input type="email" required value={form.email} onChange={set("email")} data-testid="contact-email" className={inputCls} />
        </div>
      </div>
      <div className="grid gap-8 sm:grid-cols-2">
        <div>
          <label className={labelCls}>{t.contact.subject}</label>
          <input value={form.subject} onChange={set("subject")} data-testid="contact-subject" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>{t.contact.area}</label>
          <select value={form.area} onChange={set("area")} data-testid="contact-area" className={`${inputCls} [&>option]:bg-obsidian-soft`}>
            <option value="">{t.contact.chooseArea}</option>
            {["interior", "fashion", "realEstate", "consulting", "library"].map((a) => (
              <option key={a} value={a}>{t.areas[a].name}</option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <label className={labelCls}>{t.contact.message}</label>
        <textarea required rows={4} value={form.message} onChange={set("message")} data-testid="contact-message" className={`${inputCls} resize-none`} />
      </div>
      <button
        type="submit"
        disabled={status === "loading"}
        data-testid="contact-submit"
        className="group flex items-center gap-3 bg-champagne px-8 py-4 text-xs uppercase tracking-[0.2em] text-obsidian transition-colors duration-300 hover:bg-champagne-light disabled:opacity-60"
      >
        {status === "loading" ? t.contact.sending : t.contact.send}
        <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
      </button>
    </form>
  );
};

const Booking = () => {
  const { t } = useLang();
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [time, setTime] = useState("");
  const [details, setDetails] = useState({ name: "", email: "", phone: "", topic: "consulting", note: "" });
  const [status, setStatus] = useState("idle");
  const [success, setSuccess] = useState(false);

  const dateStr = date ? format(date, "yyyy-MM-dd") : "";

  useEffect(() => {
    if (!dateStr) return;
    setTime("");
    getAvailability(dateStr).then((d) => setSlots(d.slots)).catch(() => setSlots([]));
  }, [dateStr]);

  const set = (k) => (e) => setDetails((d) => ({ ...d, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!dateStr || !time) return;
    setStatus("loading");
    try {
      await createBooking({ ...details, date: dateStr, time });
      setSuccess(true);
    } catch {
      setStatus("idle");
      toast.error(t.booking.error);
    }
  };

  if (success) {
    return (
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-3 border border-champagne/40 px-6 py-8 text-champagne" data-testid="booking-success">
        <Check size={22} /> <span className="text-base">{t.booking.success}</span>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-10" data-testid="booking-form">
      <div className="grid gap-10 lg:grid-cols-2">
        <div>
          <label className={labelCls}>{t.booking.selectDate}</label>
          <div className="mt-3 inline-block border border-white/10 bg-obsidian-soft p-2">
            <Calendar
              mode="single"
              selected={date}
              onSelect={setDate}
              disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0)) || d.getDay() === 0 || d.getDay() === 6}
              data-testid="booking-calendar"
            />
          </div>
        </div>
        <div>
          <label className={labelCls}>{t.booking.selectTime}</label>
          {!dateStr ? (
            <p className="mt-3 text-sm font-light text-white/40">{t.booking.noSlots}</p>
          ) : (
            <div className="mt-3 grid grid-cols-3 gap-3" data-testid="booking-slots">
              {slots.map((s) => (
                <button
                  key={s.time}
                  type="button"
                  disabled={!s.available}
                  onClick={() => setTime(s.time)}
                  data-testid={`slot-${s.time}`}
                  className={`border py-3 text-sm transition-colors duration-300 ${
                    time === s.time
                      ? "border-champagne bg-champagne text-obsidian"
                      : s.available
                      ? "border-white/20 text-white hover:border-champagne hover:text-champagne"
                      : "cursor-not-allowed border-white/5 text-white/20 line-through"
                  }`}
                >
                  {s.time}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="grid gap-8 sm:grid-cols-2">
        <div>
          <label className={labelCls}>{t.contact.name}</label>
          <input required value={details.name} onChange={set("name")} data-testid="booking-name" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>{t.contact.email}</label>
          <input type="email" required value={details.email} onChange={set("email")} data-testid="booking-email" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>{t.booking.phone}</label>
          <input value={details.phone} onChange={set("phone")} data-testid="booking-phone" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>{t.booking.topic}</label>
          <select value={details.topic} onChange={set("topic")} data-testid="booking-topic" className={`${inputCls} [&>option]:bg-obsidian-soft`}>
            {Object.entries(t.booking.topics).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <label className={labelCls}>{t.booking.note}</label>
        <textarea rows={3} value={details.note} onChange={set("note")} data-testid="booking-note" className={`${inputCls} resize-none`} />
      </div>

      <button
        type="submit"
        disabled={status === "loading" || !time}
        data-testid="booking-submit"
        className="group flex items-center gap-3 bg-champagne px-8 py-4 text-xs uppercase tracking-[0.2em] text-obsidian transition-colors duration-300 hover:bg-champagne-light disabled:cursor-not-allowed disabled:opacity-40"
      >
        <CalIcon size={16} />
        {status === "loading" ? t.booking.booking : t.booking.confirm}
      </button>
    </form>
  );
};

const Contact = () => {
  const { t } = useLang();
  const [tab, setTab] = useState("contact");

  return (
    <div data-testid="contact-page" className="bg-obsidian">
      <Seo pageKey="contact" />
      <section className="mx-auto max-w-[1400px] px-6 pt-40 pb-12 md:px-12 md:pt-52">
        <span className="text-xs uppercase tracking-[0.3em] text-champagne">{t.contact.overline}</span>
        <h1 className="mt-6 max-w-3xl font-serif text-6xl font-light leading-[0.95] tracking-tight text-white md:text-8xl">
          <MaskedLines lines={[t.contact.title]} startDelay={0.2} />
        </h1>

        <div className="mt-14 flex gap-8 border-b border-white/10">
          {[
            { k: "contact", label: t.contact.overline },
            { k: "booking", label: t.booking.overline },
          ].map((tb) => (
            <button
              key={tb.k}
              onClick={() => setTab(tb.k)}
              data-testid={`tab-${tb.k}`}
              className={`relative pb-4 text-xs uppercase tracking-[0.25em] transition-colors ${tab === tb.k ? "text-champagne" : "text-white/45 hover:text-white"}`}
            >
              {tb.label}
              {tab === tb.k && <motion.span layoutId="tab-underline" className="absolute inset-x-0 -bottom-px h-px bg-champagne" />}
            </button>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-6 pb-40 md:px-12">
        <Reveal key={tab}>
          <p className="mb-12 max-w-xl text-base font-light leading-relaxed text-white/55 md:text-lg">
            {tab === "contact" ? t.contact.sub : t.booking.sub}
          </p>
          {tab === "contact" ? <ContactForm /> : <Booking />}
        </Reveal>
      </section>

      <FaqSection pageKey="contact" />
    </div>
  );
};

export default Contact;
